// A small external store for "which delivery days exist": one entry per signed-in user and
// country, shared by every component, fetched at most once at a time, and refreshed after a TTL.
// It is built for React's useSyncExternalStore: get() returns the same object until the entry
// changes, and every change replaces the object and notifies subscribers.
import type { CountryCode } from '../config/countries';
import { indexAvailability } from '../utils/availability';
import type { AvailabilityIndex } from '../utils/availability';
import { fetchAvailability } from './availabilityApi';
import { ApiError, isAbortError } from './http';

export type AvailabilityState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; index: AvailabilityIndex; stale: boolean }
  | { status: 'no-access'; message: string } // 403
  | { status: 'unavailable' } // 404: the endpoint is not on this host (prod Express)
  | { status: 'error'; message: string; httpStatus: number | null; retryable: boolean }; // 400/401 not retryable; 5xx and network retryable

export interface AvailabilityStore {
  /** Stable object identity until the entry changes; the IDLE constant when there is none. */
  get(uid: string, c: CountryCode): AvailabilityState;
  /** Fetch if idle or failed, or if a settled entry is older than the TTL. Never starts a second fetch while one is running. */
  ensure(uid: string, c: CountryCode): void;
  /** Drop the entry and fetch again (the Retry button). */
  refresh(uid: string, c: CountryCode): void;
  subscribe(listener: () => void): () => void;
}

export interface AvailabilityStoreOptions {
  fetcher: (c: CountryCode, signal: AbortSignal) => Promise<unknown[]>;
  ttlMs: number;
  now?: () => number;
}

const IDLE: AvailabilityState = Object.freeze({ status: 'idle' as const });
const LOADING: AvailabilityState = Object.freeze({ status: 'loading' as const });

const DEFAULT_ERROR_MESSAGE = 'Could not load delivery days.';

interface Entry {
  state: AvailabilityState;
  /** When the state was last settled by a response; 0 before the first one. */
  fetchedAt: number;
  /** Present while a fetch is running. */
  controller?: AbortController;
}

interface ResettableStore extends AvailabilityStore {
  reset(): void;
}

function toFailureState(error: unknown): AvailabilityState {
  if (error instanceof ApiError) {
    if (error.status === 403) return { status: 'no-access', message: error.message };
    if (error.status === 404) return { status: 'unavailable' };
    return {
      status: 'error',
      message: error.message,
      httpStatus: error.status === 0 ? null : error.status,
      retryable: error.status === 0 || error.status >= 500,
    };
  }
  // Not an ApiError: nothing says retrying is pointless, and a retry costs nothing.
  return {
    status: 'error',
    message: error instanceof Error && error.message ? error.message : DEFAULT_ERROR_MESSAGE,
    httpStatus: null,
    retryable: true,
  };
}

function build(o: AvailabilityStoreOptions): ResettableStore {
  const now = o.now ?? Date.now;
  const entries = new Map<string, Entry>();
  const listeners = new Set<() => void>();

  const keyOf = (uid: string, c: CountryCode) => `${uid}|${c}`;

  function notify() {
    Array.from(listeners).forEach((listener) => listener());
  }

  function start(key: string, c: CountryCode, prev: Entry | undefined) {
    const controller = new AbortController();
    const settledAt = prev?.fetchedAt ?? 0;

    // What people see while the fetch runs: the old data marked stale, the old no-access or
    // unavailable verdict unchanged, otherwise the loading state.
    let state: AvailabilityState = LOADING;
    if (prev?.state.status === 'ready') {
      state = prev.state.stale ? prev.state : { ...prev.state, stale: true };
    } else if (prev && (prev.state.status === 'no-access' || prev.state.status === 'unavailable')) {
      state = prev.state;
    }
    entries.set(key, { state, fetchedAt: settledAt, controller });
    if (state !== prev?.state) notify();

    const current = () => {
      const entry = entries.get(key);
      return entry && entry.controller === controller ? entry : null;
    };

    const fail = (error: unknown) => {
      const entry = current();
      if (!entry) return; // superseded by refresh() or reset()
      if (isAbortError(error)) {
        // Not ours: refresh() and reset() drop the entry before they abort, so an abort that gets
        // here came from outside the store. Release the in-flight marker so the next ensure()
        // can fetch again, and take 'loading' off the screen: nothing is running any more. The
        // entry goes back to what it was before this fetch (idle for a first fetch, the earlier
        // error for a retry). A ready or no-access state was never replaced, so it stays.
        const before = prev?.state;
        const restored =
          entry.state === LOADING ? (before && before !== LOADING ? before : IDLE) : entry.state;
        entries.set(key, { state: restored, fetchedAt: entry.fetchedAt });
        if (restored !== entry.state) notify();
        return;
      }
      const failure = toFailureState(error);
      if (entry.state.status === 'ready' && failure.status === 'error') {
        // A failed background refresh must not wipe the dates a person is working with. The
        // state stays ready and stale, fetchedAt stays old, so the next ensure() tries again.
        entries.set(key, { state: entry.state, fetchedAt: entry.fetchedAt });
        return;
      }
      entries.set(key, { state: failure, fetchedAt: now() });
      notify();
    };

    let pending: Promise<unknown[]>;
    try {
      pending = o.fetcher(c, controller.signal);
    } catch (error) {
      pending = Promise.reject(error);
    }
    pending.then((rows) => {
      if (!current()) return;
      const at = now();
      let index: AvailabilityIndex;
      try {
        index = indexAvailability(c, Array.isArray(rows) ? rows : [], at).index;
      } catch (error) {
        fail(error);
        return;
      }
      entries.set(key, { state: { status: 'ready', index, stale: false }, fetchedAt: at });
      notify();
    }, fail);
  }

  function get(uid: string, c: CountryCode): AvailabilityState {
    return entries.get(keyOf(uid, c))?.state ?? IDLE;
  }

  function ensure(uid: string, c: CountryCode) {
    const key = keyOf(uid, c);
    const entry = entries.get(key);
    if (entry?.controller) return; // already running
    if (entry) {
      const settled =
        entry.state.status === 'ready' ||
        entry.state.status === 'no-access' ||
        entry.state.status === 'unavailable';
      if (settled && now() - entry.fetchedAt < o.ttlMs) return;
    }
    start(key, c, entry);
  }

  function refresh(uid: string, c: CountryCode) {
    const key = keyOf(uid, c);
    entries.get(key)?.controller?.abort();
    entries.delete(key);
    ensure(uid, c);
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function reset() {
    entries.forEach((entry) => entry.controller?.abort());
    entries.clear();
    notify();
  }

  // Plain functions, not methods that rely on `this`: callers may hand them around detached.
  return { get, ensure, refresh, subscribe, reset };
}

export function createAvailabilityStore(o: AvailabilityStoreOptions): AvailabilityStore {
  return build(o);
}

const singleton = build({ fetcher: fetchAvailability, ttlMs: 15 * 60 * 1000 });

/** The app-wide store: entries live for 15 minutes. */
export const availabilityStore: AvailabilityStore = singleton;

/** Test helper: abort running fetches and forget every entry. */
export function resetAvailabilityStoreForTests(): void {
  singleton.reset();
}
